import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'contexts/AuthContext';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Helmet } from 'react-helmet';
import { TextV2, TabView } from '@insights-ltd/design-library/components';
import FullHeight from 'components/FullHeight';
import Breadcrumbs from 'components/Breadcrumbs';
import PermissionGuard from 'components/PermissionGuard';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import Button from '@mui/material/Button';
import { Link as RouterLink } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import { Theme } from '@mui/material/styles';
import GroupsTab from './GroupsTab';
import OrganisationsTab from './OrganisationsTab';

const Organisations = () => {
  type TabType = 'organisations' | 'groups';
  const ORGANISATIONS: TabType = 'organisations';
  const GROUPS: TabType = 'groups';
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState<TabType>(ORGANISATIONS);
  const canSeeGroups = user?.permissions.Organisation_Group_ReadAll !== 'None';

  const buttonStyles = (theme: Theme) => {
    return {
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2.5),
      '&:hover, &:focus': {
        textDecoration: 'underline',
      },
    };
  };

  const handleTabChange = (_event: any, val: TabType) => {
    setTabValue(val);
  };
  const orgTabHeading = t(
    'ui.event-management.organisations.tab.label.organisations',
  );
  const groupsTabHeader = t(
    'ui.event-management.organisations.tab.label.groups',
  );

  const options = [
    {
      header: orgTabHeading,
      value: ORGANISATIONS,
      content: <OrganisationsTab />,
    },
    ...(canSeeGroups
      ? [
          {
            header: groupsTabHeader,
            value: GROUPS,
            content: <GroupsTab />,
          },
        ]
      : []),
  ];
  return (
    <>
      <Helmet>
        <title>{t('ui.event-management.title.organisations')}</title>
      </Helmet>
      <FullHeight>
        <Container
          maxWidth="lg"
          sx={(theme) => ({
            marginBottom: theme.spacing(spacingSizeMap.L),
          })}
        >
          <Box
            sx={{
              py: (theme) => theme.spacing(spacingSizeMap.M),
            }}
          >
            <Breadcrumbs
              crumbs={{
                '/': t('ui.event-management.dashboard.home.title'),
              }}
              activeText={t('ui.event-management.organisations.title')}
            />
          </Box>
          <Box
            sx={{
              mb: (theme) => theme.spacing(spacingSizeMap.S),
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Grid size={{ xs: 12, md: 'auto' }}>
                <TextV2 variant="h2">
                  {t('ui.event-management.organisations.title')}
                </TextV2>
              </Grid>
              <PermissionGuard
                requiredPermissions={[
                  { action: 'Organisation_Create', scope: 'Global' },
                ]}
              >
                <Grid
                  sx={(theme) => ({
                    '& *:nth-of-type(2)': {
                      marginLeft: theme.spacing(spacingSizeMap.XS),
                    },
                  })}
                  size={{ xs: 12, md: 'auto' }}
                >
                  <Button
                    component={RouterLink}
                    to="/organisations/add"
                    variant="contained"
                    color="primary"
                    sx={(theme) => ({ ...buttonStyles(theme) })}
                  >
                    <AddIcon />
                    &nbsp;
                    {t('ui.event-management.organisations.create-organisation')}
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/groups/add"
                    variant="outlined"
                    color="primary"
                    sx={(theme) => ({
                      ...buttonStyles(theme),
                      border: '2px solid',
                      lineHeight: '22px',
                      '&.MuiButtonBase-root:hover': {
                        backgroundColor: 'transparent',
                        border: '2px solid',
                      },
                    })}
                  >
                    <AddIcon />
                    &nbsp;
                    {t('ui.event-management.organisations.add-group')}
                  </Button>
                </Grid>
              </PermissionGuard>
            </Grid>
          </Box>
          <TabView
            value={tabValue}
            options={options}
            onChange={handleTabChange}
          />
        </Container>
      </FullHeight>
    </>
  );
};

export default Organisations;
